import { Suspense, useEffect, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUndoRedo } from '../context/UndoRedoContext';
import { useToast } from '../context/ToastContext';
import { DASHBOARD_PAGE_TITLES, PAGE_TITLE_ICONS } from '../dashboard/pageTitles';
import { dashboardPageKeyFromPathname } from '../lib/dashboardPath';
import {
  BASIC_EDITOR_FILTER_STATE_EVENT,
  BASIC_EDITOR_TOOLBAR_EVENT,
  type BasicEditorFilterStateDetail,
  type BasicEditorToolbarCommand,
} from '../dashboard/basicEditorToolbar';
import {
  dispatchFloatingFiltersToggle,
  readFloatingFiltersEnabled,
  writeFloatingFiltersEnabled,
} from '../dashboard/floatingFilters';
import {
  EPG_EDITOR_FILTER_STATE_EVENT,
  EPG_EDITOR_TOOLBAR_EVENT,
  type EpgEditorFilterStateDetail,
  type EpgEditorToolbarCommand,
} from '../dashboard/epgEditorEvents';
import {
  CATEGORY_EDITOR_SHOW_DELETED_STATE_EVENT,
  CATEGORY_EDITOR_TOOLBAR_EVENT,
  type CategoryEditorShowDeletedStateDetail,
} from '../dashboard/categoryEditorEvents';
import { useDashboardToolModals } from '../context/DashboardModalsContext';
import { dispatchVodCreateMovie, dispatchVodCreateSeries } from '../lib/vodToolbarEvents';
import {
  DASHBOARD_SEARCH_EVENT,
  rememberDashboardSearchQuery,
} from '../hooks/useDashboardGridSearch';
import DashboardRouteErrorBoundary from '../components/DashboardRouteErrorBoundary';

function toTwoDigits(value: string | undefined): string {
  const n = Number(value ?? '');
  if (!Number.isFinite(n) || n < 0) return '00';
  return String(Math.floor(n)).padStart(2, '0');
}

/** Sidebar version format: vMM.mm.pp (from package.json semver). */
const DASHBOARD_VERSION = (() => {
  const [major, minor, patch] = __APP_VERSION__.split('.');
  return `${toTwoDigits(major)}.${toTwoDigits(minor)}.${toTwoDigits(patch)}`;
})();

function sectionSubClass(section: 'ch' | 'mov' | 'ser' | 'neutral', isActive: boolean): string {
  const accent =
    section === 'ch'
      ? 'text-orange'
      : section === 'mov'
        ? 'text-purple'
        : section === 'ser'
          ? 'text-teal'
          : '';
  return `nav-link py-1 ${isActive ? `active${accent ? ` ${accent}` : ''}` : ''}`;
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const { canUndo, canRedo, busy: undoBusy, undo, redo } = useUndoRedo();
  const { openToolModal } = useDashboardToolModals();
  const navigate = useNavigate();
  const location = useLocation();
  const [night, setNight] = useState(() => localStorage.getItem('nightMode') === 'true');
  const [search, setSearch] = useState('');
  const [mobileNav, setMobileNav] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [duplicatesFilterActive, setDuplicatesFilterActive] = useState(false);
  const [epgWithoutFilterActive, setEpgWithoutFilterActive] = useState(false);
  const [categoryIncludeDeleted, setCategoryIncludeDeleted] = useState(false);
  const [floatingFiltersOn, setFloatingFiltersOn] = useState(readFloatingFiltersEnabled);
  /**
   * NAS / Argon: `body.g-sidenav-pinned` = szeroki panel (250px, widać „M3UStudio” i etykiety).
   * Bez pinu przy `g-sidenav-hidden` = wąski rail 62px, same ikony; rozwinięcie na :hover lub po pin.
   */
  const [sidenavPinned, setSidenavPinned] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem('iptv-dashboard-sidenav-pinned') === 'true',
  );

  const path = location.pathname;
  const pageKey = dashboardPageKeyFromPathname(path);
  const pageTitle = DASHBOARD_PAGE_TITLES[pageKey] || 'Dashboard';
  const titleIcon = PAGE_TITLE_ICONS[pageKey] || { icon: 'fa-pencil-alt', style: 'fas' as const };
  /** Same as legacy `loadPlaylistManager`: only Playlist manager + night mode in sidebar; strip editor top actions. */
  const isPlaylistManager = pageKey === 'playlist-manager';
  /** Advanced / More: bulk narzędzia + expand/collapse — wszystkie edytory TV (channel-*), nie tylko Basic. */
  const showChannelTopMenus = pageKey.startsWith('channel-');
  const isCategoryEditor = pageKey === 'channel-category';
  const isRecycleEditor = pageKey === 'channel-recycle';
  const showAdvancedMenu = showChannelTopMenus;
  const showMoreSettingsMenu = showChannelTopMenus && !isRecycleEditor;
  const showExtendedAdvancedOptions = showChannelTopMenus && !isCategoryEditor;
  const showUndoRedo = pageKey.startsWith('channel-');
  const isMovieBasic = pageKey === 'movie-basic';
  const isSeriesBasic = pageKey === 'series-basic';
  const showVodCreate = isMovieBasic || isSeriesBasic;
  const isUserManage = pageKey === 'user-manage';
  const isAutoNew = pageKey === 'auto-new';
  const userLimit =
    Number(
      (user?.managed_users as number | string | undefined) ??
        (user?.managed_users_limit as number | string | undefined) ??
        (user?.max_managed_users as number | string | undefined) ??
        0,
    ) || 0;
  const userCurrent =
    Number(
      (user?.users_count as number | string | undefined) ??
        (user?.managed_users_count as number | string | undefined) ??
        (user?.clients_count as number | string | undefined) ??
        0,
    ) || 0;
  const showUserCounter = isUserManage && userLimit > 0;
  const showUserMoreSettings = isUserManage;
  /** Jak NAS `showEpgNavButtons()` — osobne akcje w pasku zamiast samych „Advanced / More”. */
  const isEpgEditor = pageKey === 'channel-epg';

  const [openChannels, setOpenChannels] = useState(path.includes('/channel-'));
  const [openMovies, setOpenMovies] = useState(path.includes('/movie-'));
  const [openSeries, setOpenSeries] = useState(path.includes('/series-'));
  const [openUsers, setOpenUsers] = useState(path.includes('/user-'));
  const [openAuto, setOpenAuto] = useState(path.includes('/auto-'));

  useEffect(() => {
    document.body.classList.toggle('night-mode', night);
    document.documentElement.setAttribute('data-bs-theme', night ? 'dark' : 'light');
    localStorage.setItem('nightMode', night ? 'true' : 'false');
  }, [night]);

  useEffect(() => {
    if (path.includes('/channel-')) setOpenChannels(true);
    if (path.includes('/movie-')) setOpenMovies(true);
    if (path.includes('/series-')) setOpenSeries(true);
    if (path.includes('/user-')) setOpenUsers(true);
    if (path.includes('/auto-')) setOpenAuto(true);
  }, [path]);

  useEffect(() => {
    setMobileNav(false);
    setNotifOpen(false);
  }, [path]);

  useEffect(() => {
    if (pageKey !== 'channel-basic') {
      setDuplicatesFilterActive(false);
    }
    if (pageKey !== 'channel-epg') {
      setEpgWithoutFilterActive(false);
    }
    if (pageKey !== 'channel-category') {
      setCategoryIncludeDeleted(false);
    }
  }, [pageKey]);

  useEffect(() => {
    const onFilterState = (ev: Event) => {
      const detail = (ev as CustomEvent<BasicEditorFilterStateDetail>).detail;
      setDuplicatesFilterActive(Boolean(detail?.duplicatesActive));
    };
    window.addEventListener(BASIC_EDITOR_FILTER_STATE_EVENT, onFilterState);
    return () => window.removeEventListener(BASIC_EDITOR_FILTER_STATE_EVENT, onFilterState);
  }, []);

  useEffect(() => {
    const onEpgFilter = (ev: Event) => {
      const detail = (ev as CustomEvent<EpgEditorFilterStateDetail>).detail;
      setEpgWithoutFilterActive(Boolean(detail?.withoutEpgActive));
    };
    window.addEventListener(EPG_EDITOR_FILTER_STATE_EVENT, onEpgFilter);
    return () => window.removeEventListener(EPG_EDITOR_FILTER_STATE_EVENT, onEpgFilter);
  }, []);

  useEffect(() => {
    const onCatDeleted = (ev: Event) => {
      const detail = (ev as CustomEvent<CategoryEditorShowDeletedStateDetail>).detail;
      setCategoryIncludeDeleted(Boolean(detail?.includeDeleted));
    };
    window.addEventListener(CATEGORY_EDITOR_SHOW_DELETED_STATE_EVENT, onCatDeleted);
    return () => window.removeEventListener(CATEGORY_EDITOR_SHOW_DELETED_STATE_EVENT, onCatDeleted);
  }, []);

  useEffect(() => {
    document.body.classList.add('g-sidenav-hidden');
    return () => {
      document.body.classList.remove('g-sidenav-hidden');
      document.body.classList.remove('g-sidenav-pinned');
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('g-sidenav-pinned', sidenavPinned);
    try {
      localStorage.setItem('iptv-dashboard-sidenav-pinned', sidenavPinned ? 'true' : 'false');
    } catch {
      /* ignore */
    }
  }, [sidenavPinned]);

  function basicToolbarCommand(command: BasicEditorToolbarCommand) {
    const groupOnly =
      command === 'expand-groups' || command === 'collapse-groups';
    if (groupOnly) {
      // Dispatch globally; active editors with grouped rows decide how to handle it.
      window.dispatchEvent(new CustomEvent(BASIC_EDITOR_TOOLBAR_EVENT, { detail: { command } }));
      return;
    }
    if (pageKey !== 'channel-basic') {
      showToast('Open Basic editor (Channels) to use this option.', 'info');
      return;
    }
    window.dispatchEvent(new CustomEvent(BASIC_EDITOR_TOOLBAR_EVENT, { detail: { command } }));
  }

  function epgToolbarCommand(command: EpgEditorToolbarCommand) {
    if (pageKey !== 'channel-epg') {
      showToast('Open EPG editor to use this option.', 'info');
      return;
    }
    window.dispatchEvent(new CustomEvent(EPG_EDITOR_TOOLBAR_EVENT, { detail: { command } }));
  }

  function toggleFloatingFilters(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    const next = !floatingFiltersOn;
    writeFloatingFiltersEnabled(next);
    setFloatingFiltersOn(next);
    dispatchFloatingFiltersToggle(next);
  }

  function onSearchChange(v: string) {
    setSearch(v);
    rememberDashboardSearchQuery(v);
    window.dispatchEvent(new CustomEvent(DASHBOARD_SEARCH_EVENT, { detail: v }));
  }

  function onSearchKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') onSearchChange('');
  }

  const subscriptionLabel =
    (user?.subscription as string) ||
    (user?.subscription_tier as string) ||
    (user?.plan as string) ||
    'BASIC';
  const maxChannels = user?.max_channels != null ? String(user.max_channels) : '1000';
  const maxMovies = user?.max_movies != null ? String(user.max_movies) : '1000';
  const expiry =
    (user?.expiry as string) ||
    (user?.expiry_date as string) ||
    (user?.subscription_expiry as string) ||
    '—';

  function scrollToManualAnchor() {
    document.getElementById('iptv-manual-updater-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function showSoon(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    showToast('Feature will be implemented soon.', 'info');
  }

  return (
    <div id="react-dashboard-root" className={mobileNav ? 'mobile-nav-open' : ''} style={{ minHeight: '100vh' }}>
      {/* Mobile backdrop */}
      {mobileNav && (
        <button
          type="button"
          aria-label="Close menu"
          className="d-lg-none position-fixed top-0 start-0 w-100 h-100 border-0 p-0"
          style={{ zIndex: 1030, background: 'rgba(0,0,0,0.35)' }}
          onClick={() => setMobileNav(false)}
        />
      )}

      <nav
        className="sidenav navbar navbar-vertical fixed-left navbar-expand-xs navbar-light bg-white sidebar legacy-sidebar"
        id="sidenav-main"
      >
        <div className="scroll-wrapper scrollbar-inner" style={{ height: '100%' }}>
          <div className="scrollbar-inner scroll-content" style={{ height: '100%' }}>
            <div className="sidenav-header d-flex align-items-center">
              <NavLink className="navbar-brand" to="/dashboard/playlist-manager" onClick={() => setMobileNav(false)}>
                <span style={{ color: '#5e72e4', fontSize: 24, fontWeight: 'bold' }}>M3UStudio</span>
              </NavLink>
              <div className="ms-auto">
                <button
                  type="button"
                  className={`sidenav-toggler d-none d-xl-block border-0 bg-transparent p-0 ${sidenavPinned ? 'active' : ''}`}
                  data-action="sidenav-unpin"
                  data-target="#sidenav-main"
                  aria-label={sidenavPinned ? 'Unpin sidebar (narrow icons)' : 'Pin sidebar (full width)'}
                  aria-pressed={sidenavPinned}
                  onClick={() => setSidenavPinned((v) => !v)}
                >
                  <div className="sidenav-toggler-inner">
                    <i className="sidenav-toggler-line" />
                    <i className="sidenav-toggler-line" />
                    <i className="sidenav-toggler-line" />
                  </div>
                </button>
              </div>
            </div>

            <div className="navbar-inner">
              <div className="collapse show navbar-collapse">
                <ul className="navbar-nav">
                  <li className="nav-item">
                    <NavLink
                      to="/dashboard/playlist-manager"
                      end
                      className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                      onClick={() => setMobileNav(false)}
                    >
                      <i className="far fa-save text-primary" />
                      <span className="nav-link-text">Playlist manager</span>
                    </NavLink>
                  </li>

                  <li className={`nav-item${isPlaylistManager ? ' d-none' : ''}`}>
                    <a
                      href="#channels"
                      className={`nav-link d-flex align-items-center ${openChannels ? '' : 'collapsed'}`}
                      data-bs-toggle="collapse"
                      aria-expanded={openChannels}
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenChannels(!openChannels);
                      }}
                    >
                      <i className="fas fa-tv text-orange" />
                      <span className="nav-link-text">Channels</span>
                    </a>
                  <div className={`collapse navbar-collapse ${openChannels ? 'show' : ''}`} id="channelsCollapseReact">
                    <ul className="nav-sm flex-column nav">
                      <Sub
                        route="/dashboard/channel-basic"
                        section="ch"
                        label="Basic editor"
                        icon="fa-pencil-alt"
                        onNav={() => setMobileNav(false)}
                      />
                      <Sub route="/dashboard/channel-number" section="ch" label="Number editor" icon="fa-sort-numeric-down" onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/channel-logo" section="ch" label="Logo editor" icon="fa-image" onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/channel-epg" section="ch" label="EPG editor" icon="fa-calendar-alt" onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/channel-category" section="ch" label="Category editor" icon="fa-object-group" onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/channel-manual" section="ch" label="Manual updater" icon="fa-sync-alt" onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/channel-recycle" section="ch" label="Recycle bin" icon="fa-trash-alt" onNav={() => setMobileNav(false)} />
                    </ul>
                  </div>
                </li>

                <li className={`nav-item${isPlaylistManager ? ' d-none' : ''}`}>
                  <a
                    href="#movies"
                    className={`nav-link d-flex align-items-center ${openMovies ? '' : 'collapsed'}`}
                    aria-expanded={openMovies}
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenMovies(!openMovies);
                    }}
                  >
                    <i className="fas fa-film text-purple" />
                    <span className="nav-link-text">Movies</span>
                  </a>
                  <div className={`collapse navbar-collapse ${openMovies ? 'show' : ''}`}>
                    <ul className="nav-sm flex-column nav">
                      <Sub route="/dashboard/movie-basic" section="mov" label="Basic editor" icon="fa-pencil-alt" onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/movie-cover" section="mov" label="Cover editor" icon="fa-image" far onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/movie-info" section="mov" label="Info search" icon="fa-info-circle" onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/movie-category" section="mov" label="Category editor" icon="fa-object-group" far onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/movie-updater" section="mov" label="Manual updater" icon="fa-sync-alt" onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/movie-recycle" section="mov" label="Recycle bin" icon="fa-trash-alt" onNav={() => setMobileNav(false)} />
                    </ul>
                  </div>
                </li>

                <li className={`nav-item${isPlaylistManager ? ' d-none' : ''}`}>
                  <a
                    href="#series"
                    className={`nav-link d-flex align-items-center ${openSeries ? '' : 'collapsed'}`}
                    aria-expanded={openSeries}
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenSeries(!openSeries);
                    }}
                  >
                    <i className="fas fa-video text-teal" />
                    <span className="nav-link-text">TV series</span>
                  </a>
                  <div className={`collapse navbar-collapse ${openSeries ? 'show' : ''}`}>
                    <ul className="nav-sm flex-column nav">
                      <Sub route="/dashboard/series-basic" section="ser" label="Basic editor" icon="fa-pencil-alt" onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/series-cover" section="ser" label="Cover editor" icon="fa-image" far onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/series-info" section="ser" label="Info search" icon="fa-info-circle" onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/series-category" section="ser" label="Category editor" icon="fa-object-group" far onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/series-updater" section="ser" label="Manual updater" icon="fa-sync-alt" onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/series-recycle" section="ser" label="Recycle bin" icon="fa-trash-alt" onNav={() => setMobileNav(false)} />
                    </ul>
                  </div>
                </li>

                <li className={`nav-item${isPlaylistManager ? ' d-none' : ''}`}>
                  <a
                    href="#users"
                    className={`nav-link d-flex align-items-center ${openUsers ? '' : 'collapsed'}`}
                    aria-expanded={openUsers}
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenUsers(!openUsers);
                    }}
                  >
                    <i className="fas fa-users text-green" />
                    <span className="nav-link-text">User management</span>
                  </a>
                  <div className={`collapse navbar-collapse ${openUsers ? 'show' : ''}`}>
                    <ul className="nav-sm flex-column nav">
                      <Sub route="/dashboard/user-manage" section="neutral" label="Manage users" icon="fa-pencil-alt" onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/user-new" section="neutral" label="New user" icon="fa-plus" onNav={() => setMobileNav(false)} />
                    </ul>
                  </div>
                </li>

                <li className={`nav-item${isPlaylistManager ? ' d-none' : ''}`}>
                  <a
                    href="#auto"
                    className={`nav-link d-flex align-items-center ${openAuto ? '' : 'collapsed'}`}
                    aria-expanded={openAuto}
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenAuto(!openAuto);
                    }}
                  >
                    <i className="fas fa-robot text-gray" />
                    <span className="nav-link-text">Auto updater</span>
                  </a>
                  <div className={`collapse navbar-collapse ${openAuto ? 'show' : ''}`}>
                    <ul className="nav-sm flex-column nav">
                      <Sub route="/dashboard/auto-sources" section="neutral" label="Manage sources" icon="fa-link" onNav={() => setMobileNav(false)} />
                      <Sub route="/dashboard/auto-new" section="neutral" label="New updater source" icon="fa-plus" onNav={() => setMobileNav(false)} />
                    </ul>
                  </div>
                </li>

                <hr className="my-2 nightmode-switch" />
                <li className="nav-item nightmode-switch">
                  <div className="navbar-heading px-3 py-2 text-muted d-flex justify-content-between align-items-center w-100">
                    <span className="docs-normal fw-semibold">
                      <i className="fas fa-moon" />
                      NIGHT MODE
                    </span>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="nightModeToggleReact"
                        checked={night}
                        onChange={(e) => setNight(e.target.checked)}
                        style={{ width: '2.4rem', height: '1.25rem', cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                </li>
                <hr className="my-2 nightmode-switch" />
              </ul>
            </div>

            <div className="sidebar-version px-3 py-3 text-start" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
              <a
                className="docs-normal text-muted small d-inline-block"
                id="version"
                href="#version"
                onClick={(e) => e.preventDefault()}
                style={{ textDecoration: 'none', opacity: 0.7 }}
              >
                <i className="fas fa-code-branch" />
                v{DASHBOARD_VERSION}
              </a>
            </div>
          </div>
          </div>
        </div>
      </nav>

      <div className="main-content legacy-main-content" id="legacy-main-content-react">
        <nav className="navbar-top navbar-expand-lg border-bottom navbar-dark bg-primary navbar">
          <div className="container-fluid d-flex align-items-center flex-nowrap py-0 navbar-top-container-fluid">
            <div className="collapse show navbar-collapse w-100">
              <div className="navbar-search navbar-search-light form-inline mr-sm-3 pl-1">
                <div className="form-group mb-0">
                  <div className="input-group input-group-alternative input-group-merge">
                    <span className="input-group-text">
                      <i className="fas fa-search" />
                    </span>
                    <input
                      className="form-control"
                      placeholder="Search..."
                      type="text"
                      id="globalSearch"
                      value={search}
                      onChange={(e) => onSearchChange(e.target.value)}
                      onKeyDown={onSearchKey}
                    />
                  </div>
                </div>
                <a
                  className={`nav-link mr-1 ml-1 filter-button${floatingFiltersOn ? ' active' : ''}`}
                  id="filterBtn"
                  href="#"
                  title={floatingFiltersOn ? 'Hide column filters' : 'Show column filters'}
                  onClick={toggleFloatingFilters}
                >
                  <i className="fas fa-filter" />
                </a>
              </div>

              <div className="editor-title navbar-title-center only-desktop">
                <h6 className="h2 text-white d-inline-block mb-0" id="pageTitle">
                  <i className={`${titleIcon.style} ${titleIcon.icon}`} />
                  {pageTitle}
                </h6>
              </div>

              <ul className="align-items-center ms-auto navbar-nav">
                <li className="d-xl-none nav-item">
                  {/* dashboard.html: div + sidenav-toggler (nie button) */}
                  <div
                    className="pr-3 sidenav-toggler sidenav-toggler-dark"
                    role="button"
                    tabIndex={0}
                    onClick={() => setMobileNav(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setMobileNav(true);
                      }
                    }}
                  >
                    <div className="sidenav-toggler-inner">
                      <i className="sidenav-toggler-line" />
                      <i className="sidenav-toggler-line" />
                      <i className="sidenav-toggler-line" />
                    </div>
                  </div>
                </li>
              </ul>

              <div className="navbar-toggles">
                <a
                  className={`nav-link filter-button pr-0 ${!canUndo || undoBusy ? 'no-undo' : ''}`}
                  id="undo"
                  href="#"
                  style={{ display: showUndoRedo ? undefined : 'none' }}
                  onClick={(e) => {
                    e.preventDefault();
                    void undo();
                  }}
                >
                  <i className="fas fa-undo-alt" />
                  <i className="fas fa-caret-down" />
                </a>
                <a
                  className={`nav-link filter-button pr-0 ${!canRedo || undoBusy ? 'no-undo' : ''}`}
                  id="redo"
                  href="#"
                  style={{ display: showUndoRedo ? undefined : 'none' }}
                  onClick={(e) => {
                    e.preventDefault();
                    void redo();
                  }}
                >
                  <i className="fas fa-redo-alt" />
                  <i className="fas fa-caret-down" />
                </a>
                <a
                  className="nav-link filter-button"
                  id="Searchupdates70"
                  href="#"
                  style={{ display: !isPlaylistManager && pageKey === 'channel-manual' ? 'inline-block' : 'none' }}
                  onClick={(e) => {
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent('iptv-open-manual-updater-wizard'));
                    scrollToManualAnchor();
                  }}
                >
                  <i className="fas fa-sync-alt" />
                </a>
                {showUserCounter ? (
                  <li className="dropdown nav-item text-white" id="customer-count">
                    <span className="h4 pr-2 text-white no-mobile-text">Users</span>
                    <span className="badge badge-pill badge-floating badge-primary border-white">
                      <span className="h4">
                        {`${userCurrent} / ${userLimit}`}
                      </span>
                    </span>
                  </li>
                ) : null}
                <ul className="align-items-center ms-md-0 navbar-nav">
                  <li className="dropdown nav-item">
                    <a
                      className="nav-link pr-1"
                      id="notificationsBtn"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setNotifOpen((v) => !v);
                      }}
                    >
                      <i className="fas fa-bell" />
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" id="notificationBadge" style={{ display: 'none' }}>
                        !
                      </span>
                    </a>
                  </li>
                </ul>

                <div className={`notification-window ${notifOpen ? '' : 'hidden'}`}>
                  <div className="notification-content">
                    <div className="noti-title dropdown-header">
                      <h6 className="text-overflow m-0">NOTIFICATIONS</h6>
                    </div>
                    <div className="dropdown-item text-center">
                      <span className="text-muted" id="notificationsList">
                        No notifications
                      </span>
                    </div>
                  </div>
                </div>

                <ul className="align-items-center ms-md-0 pr-2 navbar-nav">
                  <li className="dropdown nav-item">
                    <a
                      className="nav-link pr-1 dropdown-toggle d-inline-flex align-items-center gap-1"
                      id="userMenuBtn"
                      href="#"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <img
                        id="navbarUserAvatar"
                        src=""
                        alt=""
                        className="rounded-circle"
                        width={28}
                        height={28}
                        style={{ objectFit: 'cover', display: 'none' }}
                      />
                      <i className="fas fa-user" id="navbarUserIcon" />
                    </a>
                    <div className="dropdown-menu dropdown-menu-end" id="userMenu">
                      <div className="noti-title dropdown-header">
                        <h6 className="text-overflow m-0" id="userName">
                          PROFILE
                        </h6>
                      </div>
                      <a
                        href="#"
                        className="dropdown-item"
                        id="profileLink"
                        onClick={(e) => {
                          e.preventDefault();
                          openToolModal('profile');
                        }}
                      >
                        <i className="fas fa-user" />
                        <span>My profile</span>
                      </a>
                      <a
                        href="#"
                        className="dropdown-item"
                        id="guideLink"
                        onClick={(e) => {
                          e.preventDefault();
                          openToolModal('guide');
                        }}
                      >
                        <i className="fas fa-book-open" />
                        <span>Guide</span>
                      </a>
                      <div className="dropdown-divider" />
                      <div className="noti-title dropdown-header">
                        <h6 className="text-overflow m-0" id="subscriptionInfo">
                          SUBSCRIPTION: {subscriptionLabel.toUpperCase()}
                        </h6>
                      </div>
                      <a href="#" className="dropdown-item" onClick={(e) => e.preventDefault()}>
                        <span id="channelsLimit">Max TV Channels: {maxChannels}</span>
                      </a>
                      <a href="#" className="dropdown-item" onClick={(e) => e.preventDefault()}>
                        <span id="moviesLimit">Max Movies: {maxMovies}</span>
                      </a>
                      <a href="#" className="dropdown-item" onClick={(e) => e.preventDefault()}>
                        <span id="expiryDate">Expiry: {expiry}</span>
                      </a>
                      <div className="dropdown-divider" />
                      <button
                        type="button"
                        className="dropdown-item"
                        id="logoutBtn"
                        onClick={async () => {
                          await logout();
                          navigate('/login', { replace: true });
                        }}
                      >
                        <i className="fas fa-sign-out-alt" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </li>
                </ul>

                <a
                  className="nav-link filter-button"
                  id="selectEpgCountries"
                  href="#"
                  style={{ display: isEpgEditor ? 'inline-block' : 'none' }}
                  onClick={(e) => {
                    e.preventDefault();
                    openToolModal('epg-countries');
                  }}
                >
                  <i className="fas fa-globe-europe" />
                </a>
                <a
                  className="nav-link filter-button"
                  id="addExternalEpg"
                  href="#"
                  style={{ display: isEpgEditor ? 'inline-block' : 'none' }}
                  onClick={(e) => {
                    e.preventDefault();
                    openToolModal('epg-external');
                  }}
                >
                  <i className="fas fa-external-link-alt" />
                </a>
                <a
                  className="nav-link filter-button"
                  id="epgSettings"
                  href="#"
                  style={{ display: isEpgEditor ? 'inline-block' : 'none' }}
                  onClick={(e) => {
                    e.preventDefault();
                    openToolModal('epg-settings');
                  }}
                >
                  <i className="fas fa-cog" />
                </a>

                <a
                  href="#"
                  className="nav-link pr-1 pl-1"
                  title={isMovieBasic ? 'Create movie' : 'Create series'}
                  style={{ display: showVodCreate ? 'inline-block' : 'none' }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (isMovieBasic) dispatchVodCreateMovie();
                    else if (isSeriesBasic) dispatchVodCreateSeries();
                  }}
                >
                  <i className="fas fa-plus-circle" />
                  <span className="d-none d-md-inline ms-1">{isMovieBasic ? 'Create movie' : 'Create series'}</span>
                </a>

                <ul
                  id="advanced-tooltip"
                  className="nav-link filter-button navbar-nav"
                  style={{ display: showAdvancedMenu ? undefined : 'none' }}
                >
                  <li className="dropdown nav-item">
                    <a href="#" className="nav-link pr-1 pl-1 dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                      <i className="fas fa-dna" />
                    </a>
                    <div className="dropdown-menu dropdown-menu-end">
                      <div className="noti-title dropdown-header">
                        <h6 className="text-overflow m-0">Advanced Editor</h6>
                      </div>
                      <a
                        href="#"
                        className="dropdown-item"
                        onClick={(e) => {
                          e.preventDefault();
                          openToolModal('name-optimizer');
                        }}
                      >
                        Automatic name optimizer
                      </a>
                      <a
                        href="#"
                        className="dropdown-item"
                        onClick={(e) => {
                          e.preventDefault();
                          openToolModal('search-replace-name');
                        }}
                      >
                        Search & replace/remove
                      </a>
                      {showExtendedAdvancedOptions ? (
                        <a
                          href="#"
                          className="dropdown-item"
                          id="removeDuplicatesMenuItem"
                          onClick={(e) => {
                            e.preventDefault();
                            openToolModal('remove-duplicates');
                          }}
                        >
                          Remove duplicates
                        </a>
                      ) : null}
                      <a
                        href="#"
                        className="dropdown-item"
                        id="prefixSuffixMenuItem"
                        onClick={(e) => {
                          e.preventDefault();
                          openToolModal('prefix-suffix');
                        }}
                      >
                        Add / remove prefix & suffix
                      </a>
                      <a
                        href="#"
                        className="dropdown-item"
                        onClick={(e) => {
                          e.preventDefault();
                          openToolModal('case-convert');
                        }}
                      >
                        Uppercase / lowercase name converter
                      </a>
                      {showExtendedAdvancedOptions ? (
                        <>
                          <div className="dropdown-divider" />
                          <a
                            href="#"
                            className="dropdown-item"
                            id="urlEditorMenuItem"
                            onClick={(e) => {
                              e.preventDefault();
                              openToolModal('url-editor');
                            }}
                          >
                            URL editor
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              openToolModal('url-editor');
                            }}
                          >
                            Change IPTV provider details
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            id="streamFormatMenuItem"
                            onClick={(e) => {
                              e.preventDefault();
                              openToolModal('stream-format');
                            }}
                          >
                            Change stream format (MPEG-TS - HLS)
                          </a>
                        </>
                      ) : null}
                    </div>
                  </li>
                </ul>

                <ul
                  id="more-settings-tooltip"
                  className="nav-link filter-button navbar-nav"
                  style={{ display: showMoreSettingsMenu ? undefined : 'none' }}
                >
                  <li className="dropdown nav-item">
                    <a href="#" className="nav-link" data-bs-toggle="dropdown" aria-expanded="false">
                      <i className="fas fa-ellipsis-v" />
                    </a>
                    <div className="dropdown-menu dropdown-menu-end">
                      <div className="noti-title dropdown-header">
                        <h6 className="text-overflow m-0">MORE SETTINGS</h6>
                      </div>
                      {pageKey === 'channel-manual' ? (
                        <a href="#" className="dropdown-item" onClick={showSoon}>
                          New updater source
                        </a>
                      ) : null}
                      {pageKey === 'channel-epg' ? (
                        <>
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              openToolModal('epg-assignment-assistant');
                            }}
                          >
                            EPG assignment assistant
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              openToolModal('epg-creator');
                            }}
                          >
                            EPG Creator
                          </a>
                          {epgWithoutFilterActive ? (
                            <a
                              href="#"
                              className="dropdown-item"
                              onClick={(e) => {
                                e.preventDefault();
                                epgToolbarCommand('filter-clear');
                              }}
                            >
                              Show all channels
                            </a>
                          ) : (
                            <>
                              <a
                                href="#"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.preventDefault();
                                  epgToolbarCommand('filter-without-epg');
                                }}
                              >
                                Show channels without EPG
                              </a>
                              <a
                                href="#"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.preventDefault();
                                  epgToolbarCommand('filter-unassigned');
                                }}
                              >
                                Show unassigned EPG IDs
                              </a>
                            </>
                          )}
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              epgToolbarCommand('remove-unassigned-ids');
                            }}
                          >
                            Remove unassigned EPG IDs
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              epgToolbarCommand('apply-shift-from-names');
                            }}
                          >
                            Apply shift from names
                          </a>
                          <div className="dropdown-divider" />
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              openToolModal('sort-streams-categories');
                            }}
                          >
                            Sort channels & categories
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              openToolModal('copy-channels-playlist');
                            }}
                          >
                            Copy edits into another playlist
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            id="moveChannelsAnotherPlaylistMenuItem"
                            onClick={(e) => {
                              e.preventDefault();
                              openToolModal('move-channels-playlist');
                            }}
                          >
                            Move in another playlist
                          </a>
                        </>
                      ) : null}
                      {pageKey === 'channel-number' || pageKey === 'channel-logo' ? (
                        <a
                          href="#"
                          className="dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            openToolModal('sort-streams-categories');
                          }}
                        >
                          Sort channels & categories
                        </a>
                      ) : null}
                      {pageKey === 'channel-category' ? (
                        <a
                          href="#"
                          className="dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            window.dispatchEvent(
                              new CustomEvent(CATEGORY_EDITOR_TOOLBAR_EVENT, {
                                detail: { command: 'toggle-show-deleted' },
                              }),
                            );
                          }}
                        >
                          {categoryIncludeDeleted ? 'Hide deleted categories' : 'Show deleted categories'}
                        </a>
                      ) : null}
                      {(pageKey === 'channel-basic' || pageKey === 'channel-category') ? (
                        <>
                          {pageKey === 'channel-basic' ? (
                            <a
                              href="#"
                              className="dropdown-item"
                              onClick={(e) => {
                                e.preventDefault();
                                openToolModal('create-channel');
                              }}
                            >
                              Create new channel
                            </a>
                          ) : null}
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              openToolModal('create-category');
                            }}
                          >
                            Create new category
                          </a>
                          <div className="dropdown-divider" />
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              openToolModal('sort-streams-categories');
                            }}
                          >
                            Sort channels & categories
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              basicToolbarCommand('expand-groups');
                            }}
                          >
                            Expand categories
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              basicToolbarCommand('collapse-groups');
                            }}
                          >
                            Collapse categories
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              basicToolbarCommand('open-column-picker');
                            }}
                          >
                            Show / hide columns
                          </a>
                          <div className="dropdown-divider" />
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              if (duplicatesFilterActive) {
                                basicToolbarCommand('filter-clear');
                              } else {
                                openToolModal('show-only-duplicates');
                              }
                            }}
                          >
                            {duplicatesFilterActive ? 'Show all channels' : 'Show only duplicated channels'}
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              basicToolbarCommand('hide-hidden-channels');
                            }}
                          >
                            Hide hidden channels
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              basicToolbarCommand('check-streams');
                            }}
                          >
                            Check selected streams
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate('/dashboard/channel-recycle');
                            }}
                          >
                            Show deleted channels
                          </a>
                          <div className="dropdown-divider" />
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate('/dashboard/movie-basic');
                            }}
                          >
                            Move in movies
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate('/dashboard/series-basic');
                            }}
                          >
                            Move in TV series
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            onClick={(e) => {
                              e.preventDefault();
                              openToolModal('copy-channels-playlist');
                            }}
                          >
                            Copy edits into another playlist
                          </a>
                          <a
                            href="#"
                            className="dropdown-item"
                            id="moveChannelsAnotherPlaylistMenuItem"
                            onClick={(e) => {
                              e.preventDefault();
                              openToolModal('move-channels-playlist');
                            }}
                          >
                            Move in another playlist
                          </a>
                        </>
                      ) : null}
                    </div>
                  </li>
                </ul>
                {(isUserManage || isAutoNew) ? (
                  <a
                    className="nav-link filter-button"
                    id="topCreateAction"
                    href={isUserManage ? '/dashboard/user-new' : '/dashboard/auto-new'}
                    onClick={(e) => {
                      e.preventDefault();
                      void navigate(isUserManage ? '/dashboard/user-new' : '/dashboard/auto-new');
                    }}
                  >
                    <i className="fas fa-plus" />
                  </a>
                ) : null}
                {showUserMoreSettings ? (
                  <ul id="more-settings-tooltip-user" className="nav-link filter-button navbar-nav">
                    <li className="dropdown nav-item">
                      <a href="#" className="nav-link" data-bs-toggle="dropdown" aria-expanded="false">
                        <i className="fas fa-ellipsis-v" />
                      </a>
                      <div className="dropdown-menu dropdown-menu-end">
                        <div className="noti-title dropdown-header">
                          <h6 className="text-overflow m-0">More settings</h6>
                        </div>
                        <a
                          href="#"
                          className="dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            showToast('Feature will be implemented soon.', 'info');
                          }}
                        >
                          Create new user
                        </a>
                        <a
                          href="#"
                          className="dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            showToast('Feature will be implemented soon.', 'info');
                          }}
                        >
                          Add/remove categories in bulk
                        </a>
                        <a
                          href="#"
                          className="dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            showToast('Feature will be implemented soon.', 'info');
                          }}
                        >
                          Welcome information
                        </a>
                        <div className="dropdown-divider" />
                        <a
                          href="#"
                          className="dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            showToast('Feature will be implemented soon.', 'info');
                          }}
                        >
                          Export as CSV file
                        </a>
                        <a
                          href="#"
                          className="dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            showToast('Feature will be implemented soon.', 'info');
                          }}
                        >
                          Customize app (preview)
                        </a>
                        <a
                          href="#"
                          className="dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            showToast('Feature will be implemented soon.', 'info');
                          }}
                        >
                          Settings
                        </a>
                      </div>
                    </li>
                  </ul>
                ) : null}
              </div>
            </div>
          </div>
        </nav>

        <div className="container-fluid pt-1 editor-page flex-grow-1" id="contentArea">
          <Suspense
            fallback={
              <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status" />
              </div>
            }
          >
            <DashboardRouteErrorBoundary>
              <Outlet />
            </DashboardRouteErrorBoundary>
          </Suspense>
        </div>
      </div>
    </div>
  );
}

function Sub({
  route,
  section,
  label,
  icon,
  far,
  onNav,
}: {
  route: string;
  section: 'ch' | 'mov' | 'ser' | 'neutral';
  label: string;
  icon: string;
  far?: boolean;
  onNav: () => void;
}) {
  const ic = far ? 'far' : 'fas';
  return (
    <li className="nav-item">
      <NavLink
        to={route}
        className={({ isActive }) => sectionSubClass(section, isActive)}
        onClick={onNav}
      >
        <span className="sidenav-mini-icon">
          <i className={`${ic} ${icon}`} />
        </span>
        <span className="sidenav-normal">{label}</span>
      </NavLink>
    </li>
  );
}
